import { useCallback, useState } from "react";
import type { UseFlashcardActionsModel } from "../../type/flascards";
import { SWIPE_THRESHOLD } from "../../utils/config";


export const useFlashcardActions = ({
  currentIndex,
  total,
  isAutoPlaying,
  setCurrentIndex,
  setIsRevealed,
  setIsAutoPlaying,
}: UseFlashcardActionsModel) => {
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragCurrentX, setDragCurrentX] = useState<number | null>(null);

  const handleNext = useCallback(() => {
    if (currentIndex < total - 1) {
      setIsRevealed(false);
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, total, setCurrentIndex, setIsRevealed]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setIsRevealed(false);
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex, setCurrentIndex, setIsRevealed]);

  const handleToggleAutoPlay = useCallback(() => {
    if (isAutoPlaying) {
      setIsAutoPlaying(false);
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    } else {
      setIsRevealed(false);
      setIsAutoPlaying(true);
    }
  }, [isAutoPlaying, setIsAutoPlaying, setIsRevealed]);

  const startDrag = useCallback(
    (x: number) => {
      if (isAutoPlaying) return;
      setDragStartX(x);
      setDragCurrentX(x);
    },
    [isAutoPlaying]
  );

  const moveDrag = useCallback(
    (x: number) => {
      if (dragStartX === null) return;
      setDragCurrentX(x);
    },
    [dragStartX]
  );

  const endDrag = useCallback(() => {
    if (dragStartX === null || dragCurrentX === null) {
      setDragStartX(null);
      setDragCurrentX(null);
      return;
    }

    const deltaX = dragCurrentX - dragStartX;
  
    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
        if (deltaX < 0) {
        handleNext();
        } else {
        handlePrevious();
        }
    }

    setDragStartX(null);
    setDragCurrentX(null);
  }, [dragStartX, dragCurrentX, handleNext, handlePrevious]);

  return {
    startDrag,
    moveDrag,
    endDrag,
    handleNext,
    handlePrevious,
    handleToggleAutoPlay,
    dragStartX,
  };
};
